import React, { useState, useEffect, useMemo } from 'react'
import {
  Button,
} from '@goodday-clone/ui'
import {
  Plus,
  Search,
  ChevronDown,
  ArrowUpDown,
  SlidersHorizontal,
  Users,
  UserCheck,
  User,
  IndianRupee,
  TrendingUp,
  Calendar,
  Globe,
  PhoneCall,
  Mail,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { LeadDetailModal, LeadDetailData } from '@/components/sales/lead-detail-modal'
import { AddLeadModal } from '@/components/sales/add-lead-modal'
import { api } from '@/api/client'
import { LeadsSubNav } from '@/features/leads/LeadsSubNav'
import { buildAssigneeCards, UNASSIGNED, type AssigneeUser } from '@/features/leads/assigneeCards'
import { cn } from '@jira-clone/ui'

interface RepLeadItem {
  id: string
  name: string
  company: string
  assignee: string
  assignedToUserId?: string
  value: number
  status: string
  source?: string
  interest?: string
  rating?: number | null
  email?: string
  phone?: string
  notes?: string
  services?: string[]
  convertedDealId?: string
  createdAt?: string
  stage?: string
}

interface RepSummary {
  name: string
  count: number
  pipelineValue: number
  pipeline: string
  conversionPercent: number
  conversion: string
  leads: RepLeadItem[]
}

const toAssigneeId = (v: any): string | undefined => (v ? String(v?._id ?? v) : undefined)

const ASSIGNEE_AVATAR_PALETTE = [
  { bg: 'bg-[#EDE9FE] dark:bg-purple-950/60', text: 'text-[#6D28D9] dark:text-purple-300' },
  { bg: 'bg-[#DBEAFE] dark:bg-blue-950/60', text: 'text-[#2563EB] dark:text-blue-300' },
  { bg: 'bg-[#FCE7F3] dark:bg-pink-950/60', text: 'text-[#DB2777] dark:text-pink-300' },
  { bg: 'bg-[#FEF3C7] dark:bg-amber-950/60', text: 'text-[#D97706] dark:text-amber-300' },
  { bg: 'bg-[#DCFCE7] dark:bg-emerald-950/60', text: 'text-[#16A34A] dark:text-emerald-300' },
]

function getAssigneeAvatar(name: string) {
  if (name === UNASSIGNED) {
    return {
      initials: 'U',
      style: { bg: 'bg-slate-200 dark:bg-slate-800', text: 'text-slate-700 dark:text-slate-300' },
    }
  }
  const clean = name.replace(/\(@[^)]+\)/g, '').trim()
  const parts = clean.split(/\s+/).filter(Boolean)
  const initials = parts.length > 1
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : (parts[0]?.slice(0, 2) || 'A').toUpperCase()
  let hash = 0
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i)
  return {
    initials,
    style: ASSIGNEE_AVATAR_PALETTE[Math.abs(hash) % ASSIGNEE_AVATAR_PALETTE.length],
  }
}

const LEAD_AVATAR_PALETTE = [
  { bg: 'bg-purple-100 dark:bg-purple-950/60', text: 'text-purple-700 dark:text-purple-300' },
  { bg: 'bg-rose-100 dark:bg-rose-950/60', text: 'text-rose-700 dark:text-rose-300' },
  { bg: 'bg-sky-100 dark:bg-sky-950/60', text: 'text-sky-700 dark:text-sky-300' },
  { bg: 'bg-indigo-100 dark:bg-indigo-950/60', text: 'text-indigo-700 dark:text-indigo-300' },
  { bg: 'bg-emerald-100 dark:bg-emerald-950/60', text: 'text-emerald-700 dark:text-emerald-300' },
  { bg: 'bg-pink-100 dark:bg-pink-950/60', text: 'text-pink-700 dark:text-pink-300' },
  { bg: 'bg-amber-100 dark:bg-amber-950/60', text: 'text-amber-700 dark:text-amber-300' },
  { bg: 'bg-cyan-100 dark:bg-cyan-950/60', text: 'text-cyan-700 dark:text-cyan-300' },
]

function getLeadAvatar(name: string) {
  const initial = (name || 'L').trim().charAt(0).toUpperCase()
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) hash += name.charCodeAt(i)
  return {
    initial,
    style: LEAD_AVATAR_PALETTE[Math.abs(hash) % LEAD_AVATAR_PALETTE.length],
  }
}

function getTagBadgeStyle(tag: string) {
  const t = tag.toLowerCase()
  if (t.includes('market') || t.includes('growth')) return 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200/60'
  if (t.includes('web') || t.includes('design')) return 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200/60'
  if (t.includes('seo')) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200/60'
  if (t.includes('social') || t.includes('media')) return 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 border-sky-200/60'
  if (t.includes('cloud')) return 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300 border-cyan-200/60'
  if (t.includes('advertis') || t.includes('brand')) return 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border-rose-200/60'
  if (t.includes('consult') || t.includes('it')) return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-200/60'
  if (t.includes('support')) return 'bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300 border-teal-200/60'
  return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200/60'
}

function getStatusBadgeStyle(status?: string) {
  const s = (status || '').toLowerCase()
  if (s.includes('qualif')) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border-emerald-200/60'
  if (s.includes('propos') || s.includes('quote')) return 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400 border-amber-200/60'
  if (s.includes('lost') || s.includes('unqual')) return 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-400 border-rose-200/60'
  if (s.includes('won')) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border-emerald-200/60'
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700'
}

function getSourceIcon(source?: string) {
  const s = (source || '').toLowerCase()
  if (s.includes('call') || s.includes('phone')) return <PhoneCall className="h-3 w-3 text-muted-foreground/70" />
  if (s.includes('mail')) return <Mail className="h-3 w-3 text-muted-foreground/70" />
  if (s.includes('meeting')) return <Users className="h-3 w-3 text-muted-foreground/70" />
  if (s.includes('referral')) return <Users className="h-3 w-3 text-muted-foreground/70" />
  return <Globe className="h-3 w-3 text-muted-foreground/70" />
}

function formatLeadDate(dateStr?: string) {
  if (!dateStr) return '20 Oct 2026'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '20 Oct 2026'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function LeadsByAssigneePage() {
  const [allLeads, setAllLeads] = useState<RepLeadItem[]>([])
  const [assignableUsers, setAssignableUsers] = useState<AssigneeUser[]>([])
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<LeadDetailData | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [expandedReps, setExpandedReps] = useState<Record<string, boolean>>({})
  const [notification, setNotification] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSource, setSelectedSource] = useState('all')
  const [selectedStage, setSelectedStage] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [sortBy, setSortBy] = useState<'count' | 'pipeline' | 'conversion' | 'name'>('count')

  // Fetch live leads from GoodDay REST API on mount
  useEffect(() => {
    async function loadBackendLeads() {
      try {
        const data = await api.get<any>('/leads')
        const rawLeads = Array.isArray(data) ? data : (data?.data || data?.leads || [])
        if (Array.isArray(rawLeads) && rawLeads.length > 0) {
          const mapped: RepLeadItem[] = rawLeads.map((l: any, i: number) => ({
            id: l._id || l.id || `lead-${i}`,
            name: l.name,
            company: l.company || '',
            assignee: l.assignee || '',
            assignedToUserId: toAssigneeId(l.assignedToUserId),
            value: typeof l.value === 'number' ? l.value : parseFloat(l.value) || 0,
            status: l.status || 'New',
            source: l.source || 'Website',
            interest: l.interest || 'Warm',
            rating: typeof l.rating === 'number' ? l.rating : (l.rating ? parseFloat(l.rating) : null),
            email: l.email || `${l.name.toLowerCase().replace(/\s+/g, '.')}@example.com`,
            phone: l.phone || '8975929541',
            notes: l.notes || '',
            services: l.services && l.services.length > 0 ? l.services : ['Digital Marketing', 'Website'],
            convertedDealId: l.convertedDealId,
            createdAt: l.createdAt,
            stage: l.stage || l.status || 'New',
          }))
          setAllLeads(mapped)
        }
      } catch (err) {
        console.warn('Backend leads fetch note:', err)
      }
    }
    loadBackendLeads()
  }, [])

  // The people who can be given a lead, from the real user list.
  useEffect(() => {
    api.get<{ data: { id: string; name: string }[] }>('/users/assignable?for=leads')
      .then((res) => setAssignableUsers((res?.data || []).map((u) => ({ id: u.id, name: u.name }))))
      .catch(() => setAssignableUsers([]))
  }, [])

  // KPI Calculations
  const totalLeads = allLeads.length
  const assignedCount = allLeads.filter((l) => l.assignee && l.assignee !== UNASSIGNED).length
  const unassignedCount = allLeads.filter((l) => !l.assignee || l.assignee === UNASSIGNED).length
  const totalPipelineValue = allLeads.reduce((acc, curr) => acc + (curr.value || 0), 0)
  const convertedCount = allLeads.filter((l) => Boolean(l.convertedDealId)).length
  const overallConversionRate = totalLeads > 0 ? Math.round((convertedCount / totalLeads) * 100) : 0

  // Filter options
  const sourceOptions = useMemo(() => {
    const set = new Set<string>()
    allLeads.forEach((l) => { if (l.source) set.add(l.source) })
    return Array.from(set)
  }, [allLeads])

  const stageOptions = useMemo(() => {
    const set = new Set<string>()
    allLeads.forEach((l) => { if (l.stage) set.add(l.stage) })
    return Array.from(set)
  }, [allLeads])

  const statusOptions = useMemo(() => {
    const set = new Set<string>()
    allLeads.forEach((l) => { if (l.status) set.add(l.status) })
    return Array.from(set)
  }, [allLeads])

  // Filtered Leads
  const filteredLeads = useMemo(() => {
    return allLeads.filter((lead) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase()
        const matchName = lead.name.toLowerCase().includes(q)
        const matchCompany = lead.company.toLowerCase().includes(q)
        const matchAssignee = lead.assignee.toLowerCase().includes(q)
        const matchServices = (lead.services || []).some((s) => s.toLowerCase().includes(q))
        if (!matchName && !matchCompany && !matchAssignee && !matchServices) return false
      }
      if (selectedSource !== 'all' && lead.source !== selectedSource) return false
      if (selectedStage !== 'all' && lead.stage !== selectedStage) return false
      if (selectedStatus !== 'all' && lead.status !== selectedStatus) return false
      return true
    })
  }, [allLeads, searchQuery, selectedSource, selectedStage, selectedStatus])

  // Rep Summaries
  const reps: RepSummary[] = useMemo(() => {
    return buildAssigneeCards(filteredLeads, assignableUsers).map((card) => ({
      name: card.name,
      count: card.count,
      pipelineValue: card.pipelineValue,
      pipeline: `₹${card.pipelineValue.toLocaleString()}`,
      conversionPercent: card.conversionPercent,
      conversion: `${card.conversionPercent}%`,
      leads: card.leads,
    }))
  }, [filteredLeads, assignableUsers])

  // Sorted Reps
  const sortedReps = useMemo(() => {
    const unassignedCard = reps.find((r) => r.name === UNASSIGNED)
    const otherCards = reps.filter((r) => r.name !== UNASSIGNED)

    otherCards.sort((a, b) => {
      if (sortBy === 'count') return b.count - a.count
      if (sortBy === 'pipeline') return b.pipelineValue - a.pipelineValue
      if (sortBy === 'conversion') return b.conversionPercent - a.conversionPercent
      if (sortBy === 'name') return a.name.localeCompare(b.name)
      return 0
    })

    // In the mockup design, Unassigned is shown as the first column!
    if (unassignedCard) {
      return [unassignedCard, ...otherCards]
    }
    return otherCards
  }, [reps, sortBy])

  const toggleExpand = (repName: string) => {
    setExpandedReps((prev) => ({ ...prev, [repName]: !prev[repName] }))
  }

  const handleLeadItemClick = (leadItem: RepLeadItem) => {
    setSelectedLead({
      id: leadItem.id,
      name: leadItem.name,
      company: leadItem.company,
      contactPerson: leadItem.name,
      role: 'Lead Prospect',
      email: leadItem.email || '',
      phone: leadItem.phone || '',
      assignee: leadItem.assignee,
      source: leadItem.source || '',
      status: leadItem.status || 'New',
      value: `₹${leadItem.value.toLocaleString()}`,
      interest: leadItem.interest || '',
      rating: leadItem.rating ?? null,
      notes: leadItem.notes || '',
      services: leadItem.services || [],
      convertedDealId: leadItem.convertedDealId,
    })
    setIsDetailModalOpen(true)
  }

  const handleLeadUpdate = (updated: LeadDetailData) => {
    const numVal = parseFloat(String(updated.value).replace(/[^0-9.]/g, '')) || 0
    setAllLeads((prev) =>
      prev.map((l) =>
        l.id === updated.id
          ? {
              ...l,
              name: updated.name,
              company: updated.company || '',
              assignee: updated.assignee || l.assignee,
              assignedToUserId: updated.assignee && updated.assignee !== l.assignee ? undefined : l.assignedToUserId,
              value: numVal,
              status: updated.status || l.status,
              source: updated.source || l.source,
              interest: updated.interest || l.interest,
              email: updated.email || l.email,
              phone: updated.phone || l.phone,
              notes: updated.notes || l.notes,
              services: updated.services || l.services,
            }
          : l
      )
    )
  }

  const handleLeadCreated = (newLead: LeadDetailData) => {
    const numVal = parseFloat(String(newLead.value).replace(/[^0-9.]/g, '')) || 0
    const item: RepLeadItem = {
      id: newLead.id,
      name: newLead.name,
      company: newLead.company || '',
      assignee: newLead.assignee || '',
      value: numVal,
      status: newLead.status || 'New',
      source: newLead.source || '',
      interest: newLead.interest || '',
      email: newLead.email,
      phone: newLead.phone,
      notes: newLead.notes,
      services: newLead.services || ['Digital Marketing', 'Website'],
    }
    setAllLeads((prev) => [item, ...prev])
    setNotification({
      type: 'success',
      message: newLead.assignee ? `Lead "${newLead.name}" added to ${newLead.assignee}'s queue!` : `Lead "${newLead.name}" added (${UNASSIGNED}).`,
    })
    setTimeout(() => setNotification(null), 5000)
  }

  return (
    <div className="space-y-5 max-w-full mx-auto">
      {/* Notifications */}
      {notification && (
        <div
          className={`p-3 rounded-xl border flex items-center justify-between text-xs transition-all ${
            notification.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
            ) : (
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
            )}
            <span>{notification.message}</span>
          </div>
          <button
            onClick={() => setNotification(null)}
            className="text-muted-foreground hover:text-foreground ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-[28px] font-bold text-foreground tracking-tight">
            Leads by Assignee
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Team lead distribution, pipeline volume and conversion performance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            className="gap-1.5 bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-sm font-medium px-4 py-2 rounded-xl h-auto"
          >
            <Plus className="h-4 w-4" /> Add Lead
          </Button>
        </div>
      </div>

      <LeadsSubNav current="assignee" />

      {/* 5 KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Leads */}
        <div className="bg-white dark:bg-card border border-slate-300 dark:border-slate-700 rounded-2xl p-4 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Total Leads</span>
            <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center">
              <Users className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-foreground">{totalLeads}</span>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1 flex items-center gap-0.5">
              <span>↑ 12%</span>
              <span className="text-muted-foreground font-normal">vs. last 7 days</span>
            </p>
          </div>
        </div>

        {/* Assigned */}
        <div className="bg-white dark:bg-card border border-slate-300 dark:border-slate-700 rounded-2xl p-4 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Assigned</span>
            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <UserCheck className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-foreground">{assignedCount}</span>
            <p className="text-xs text-muted-foreground mt-1">
              {totalLeads > 0 ? Math.round((assignedCount / totalLeads) * 100) : 0}% of total
            </p>
          </div>
        </div>

        {/* Unassigned */}
        <div className="bg-white dark:bg-card border border-slate-300 dark:border-slate-700 rounded-2xl p-4 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Unassigned</span>
            <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center">
              <User className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-foreground">{unassignedCount}</span>
            <p className="text-xs text-muted-foreground mt-1">
              {totalLeads > 0 ? Math.round((unassignedCount / totalLeads) * 100) : 0}% of total
            </p>
          </div>
        </div>

        {/* Pipeline Value */}
        <div className="bg-white dark:bg-card border border-slate-300 dark:border-slate-700 rounded-2xl p-4 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Pipeline Value</span>
            <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-950/50 text-violet-600 dark:text-violet-400 flex items-center justify-center">
              <IndianRupee className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-foreground">₹ {totalPipelineValue.toLocaleString('en-IN')}</span>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1 flex items-center gap-0.5">
              <span>↑ 18%</span>
              <span className="text-muted-foreground font-normal">vs. last 7 days</span>
            </p>
          </div>
        </div>

        {/* Conversion Rate */}
        <div className="bg-white dark:bg-card border border-slate-300 dark:border-slate-700 rounded-2xl p-4 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Conversion Rate</span>
            <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-950/50 text-teal-600 dark:text-teal-400 flex items-center justify-center">
              <TrendingUp className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-foreground">{overallConversionRate}%</span>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1 flex items-center gap-0.5">
              <span>↑ 6%</span>
              <span className="text-muted-foreground font-normal">vs. last 7 days</span>
            </p>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white dark:bg-card border border-slate-300 dark:border-slate-700 rounded-2xl p-2.5 shadow-xs flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search leads, companies, people..."
            className="w-full bg-transparent pl-9 pr-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </div>

        {/* Source Dropdown */}
        <div className="relative min-w-[130px]">
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-background px-3 py-1.5 pr-8 text-xs font-medium text-foreground focus:outline-none cursor-pointer"
          >
            <option value="all">All Sources</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        </div>

        {/* Stage Dropdown */}
        <div className="relative min-w-[130px]">
          <select
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-background px-3 py-1.5 pr-8 text-xs font-medium text-foreground focus:outline-none cursor-pointer"
          >
            <option value="all">All Stages</option>
            {stageOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        </div>

        {/* Status Dropdown */}
        <div className="relative min-w-[130px]">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-background px-3 py-1.5 pr-8 text-xs font-medium text-foreground focus:outline-none cursor-pointer"
          >
            <option value="all">All Status</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        </div>

        {/* Sort Dropdown */}
        <div className="relative min-w-[190px]">
          <div className="flex items-center rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-background px-2.5 py-1.5 gap-1.5">
            <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-full appearance-none bg-transparent pr-5 text-xs font-medium text-foreground focus:outline-none cursor-pointer"
            >
              <option value="count">Sort by: Assigned Count</option>
              <option value="pipeline">Sort by: Pipeline Value</option>
              <option value="conversion">Sort by: Conversion Rate</option>
              <option value="name">Sort by: Name</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </div>

        {/* Filter Reset Button */}
        <button
          type="button"
          onClick={() => {
            setSearchQuery('')
            setSelectedSource('all')
            setSelectedStage('all')
            setSelectedStatus('all')
            setSortBy('count')
          }}
          title="Reset Filters"
          className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-background p-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>
      </div>

      {/* Sales Rep Columns Container */}
      {sortedReps.length === 0 && (
        <p className="text-sm text-muted-foreground italic py-8 text-center">
          No sales users or leads yet.
        </p>
      )}

      {sortedReps.length > 0 && (
        <div className="overflow-x-auto pb-4 custom-scrollbar">
          <div
            className="flex gap-4 items-start"
            style={{ minWidth: `${sortedReps.length * 316}px` }}
          >
            {sortedReps.map((rep) => {
              const avatar = getAssigneeAvatar(rep.name)
              const isExpanded = !!expandedReps[rep.name]
              const visibleLeads = isExpanded ? rep.leads : rep.leads.slice(0, 4)

              return (
                <div
                  key={rep.name}
                  className="w-[308px] shrink-0 bg-[#F5F3FF] dark:bg-purple-950/20 border border-purple-200/90 dark:border-purple-900/50 rounded-2xl p-4 flex flex-col shadow-xs"
                >
                  {/* Column Header */}
                  <div className="pb-3 border-b border-purple-200/70 dark:border-purple-900/40">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className={cn(
                            'w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shrink-0 uppercase shadow-2xs',
                            avatar.style.bg,
                            avatar.style.text
                          )}
                        >
                          {avatar.initials}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-sm text-foreground truncate">{rep.name}</h3>
                          <p className="text-[11px] text-muted-foreground">{rep.count} assigned leads</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">{rep.conversion}</span>
                        <span className="text-[10px] text-muted-foreground block">Conversion</span>
                      </div>
                    </div>

                    {/* Pipeline Value */}
                    <div className="mt-3">
                      <span className="text-[10px] text-muted-foreground block font-medium">Pipeline Value</span>
                      <span className="text-sm font-bold text-foreground">{rep.pipeline}</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-purple-200/60 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden mt-2">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(100, Math.max(rep.conversionPercent, rep.count > 0 ? 8 : 0))}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Lead Cards List */}
                  <div className="space-y-2.5 mt-3 flex-1 min-h-[120px]">
                    {visibleLeads.length > 0 ? (
                      visibleLeads.map((lead) => {
                        const leadAvatar = getLeadAvatar(lead.name)
                        const tags = lead.services && lead.services.length > 0 ? lead.services : ['Digital Marketing', 'Website']

                        return (
                          <div
                            key={lead.id}
                            onClick={() => handleLeadItemClick(lead)}
                            className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-md p-3.5 transition-all cursor-pointer flex flex-col gap-2 shadow-xs"
                          >
                            {/* Top Row: Lead initial, name, company, and status pill */}
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <div
                                  className={cn(
                                    'w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0',
                                    leadAvatar.style.bg,
                                    leadAvatar.style.text
                                  )}
                                >
                                  {leadAvatar.initial}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-bold text-xs text-foreground truncate hover:text-primary transition-colors">
                                    {lead.name}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground truncate">
                                    {lead.company || lead.name}
                                  </p>
                                </div>
                              </div>

                              <span
                                className={cn(
                                  'text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 border',
                                  getStatusBadgeStyle(lead.status)
                                )}
                              >
                                {lead.status || 'New'}
                              </span>
                            </div>

                            {/* Value */}
                            <div className="text-xs font-bold text-foreground">
                              ₹ {lead.value?.toLocaleString('en-IN') || '0'}
                            </div>

                            {/* Tags / Services */}
                            {tags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {tags.map((t) => (
                                  <span
                                    key={t}
                                    className={cn(
                                      'text-[9px] font-medium px-2 py-0.5 rounded-md border',
                                      getTagBadgeStyle(t)
                                    )}
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Footer Row: Source and Date */}
                            <div className="flex items-center gap-3 text-[10px] text-muted-foreground pt-1.5 border-t border-slate-200 dark:border-slate-800">
                              <span className="inline-flex items-center gap-1 truncate max-w-[110px]">
                                {getSourceIcon(lead.source)}
                                <span className="truncate">{lead.source || 'Website'}</span>
                              </span>
                              <span className="inline-flex items-center gap-1 shrink-0 ml-auto">
                                <Calendar className="h-3 w-3 text-muted-foreground/70" />
                                <span>{formatLeadDate(lead.createdAt)}</span>
                              </span>
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <div className="py-6 px-3 text-center rounded-xl border border-dashed border-purple-300 dark:border-purple-800 bg-white/50 dark:bg-slate-900/40">
                        <p className="text-xs text-muted-foreground italic">No assigned leads</p>
                      </div>
                    )}
                  </div>

                  {/* Column Footer */}
                  {rep.leads.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleExpand(rep.name)}
                      className="mt-3 pt-2.5 border-t border-purple-200/70 dark:border-purple-900/40 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 transition-colors text-left"
                    >
                      {isExpanded ? (
                        <>Show less ↑</>
                      ) : (
                        <>View all {rep.count} leads →</>
                      )}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <LeadDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        lead={selectedLead}
        onUpdate={handleLeadUpdate}
        onDeleted={(id) => setAllLeads((prev) => prev.filter((l) => l.id !== id))}
        currentSectionName="Leads by Assignee"
        currentSectionHref="/app/leads/assignee"
      />

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onLeadCreated={handleLeadCreated}
      />
    </div>
  )
}
